/**
 * Лёгкий inline markdown-редактор: Tiptap WYSIWYG без модального chrome
 * (без header/back/save), без AI-чата. Используется в форме создания
 * записи в Descriptions-ячейке, где нужен полноценный редактор сразу,
 * а не TextArea + кнопка «развернуть».
 */
import { useEffect, useRef, useState } from 'react';
import { Button, Input, Tooltip } from 'antd';
import {
  CodeOutlined,
  EyeOutlined,
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  CheckSquareOutlined,
  LinkOutlined,
  MinusOutlined,
  UndoOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { TaskList, TaskItem } from '@tiptap/extension-list';
import { Markdown } from 'tiptap-markdown';
import { ResizableImage } from './resizableImage.ts';
import { TextColor, Highlight } from './textColor.ts';
import { ColorPickerButton } from './ColorPickerButton.tsx';
import { makeImagePasteProps, type ImageUploader } from './imagePaste.ts';

interface Props {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  /** Если задан — вставка/дроп изображений загружает их и вставляет в текст. */
  onUploadImage?: ImageUploader;
  /** Вызывается при потере фокуса редактором — с текущим markdown (автосохранение). */
  onBlur?: (markdown: string) => void;
}

export function MarkdownInlineEditor({ value, onChange, placeholder, onUploadImage, onBlur }: Props): JSX.Element {
  const [showCode, setShowCode] = useState(false);
  const [codeDraft, setCodeDraft] = useState(value);
  // onBlur держим в ref — обработчик editor.on('blur') ставится один раз,
  // иначе ловит устаревшее замыкание.
  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;
  // Флаг «следующий sync — это эхо нашего onUpdate». Нужен, потому что parent
  // нормализует значение (`.trim() === '' ? undefined : next`), и сравнение
  // по строке отказывает: editor вернул `\n`/whitespace, parent сохранил `undefined`,
  // value на следующем рендере = '' → setContent('') теряет курсор посреди ввода.
  const echoNextRef = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: { HTMLAttributes: { class: 'lens-code-block' } } }),
      Placeholder.configure({ placeholder: placeholder ?? 'Добавьте описание…' }),
      TaskList.configure({ HTMLAttributes: { class: 'lens-task-list' } }),
      TaskItem.configure({ nested: true }),
      ResizableImage.configure({ inline: false, HTMLAttributes: { class: 'lens-md-img' } }),
      TextColor,
      Highlight,
      Markdown.configure({ html: true, transformPastedText: true, transformCopiedText: true }),
    ],
    content: value,
    editable: true,
    immediatelyRender: false,
    editorProps: onUploadImage ? makeImagePasteProps(onUploadImage) : undefined,
    onUpdate: ({ editor: ed }) => {
      echoNextRef.current = true;
      const md = (ed.storage as { markdown?: { getMarkdown?: () => string } }).markdown?.getMarkdown?.() ?? ed.getText();
      onChange(md);
    },
  }, []);

  // Sync external value → editor (только для реальных внешних изменений, не для эха своего onUpdate)
  useEffect(() => {
    if (!editor) return;
    if (echoNextRef.current) {
      echoNextRef.current = false;
      return;
    }
    editor.commands.setContent(value || '', { emitUpdate: false });
  }, [value, editor]);

  // Автосохранение по потере фокуса.
  useEffect(() => {
    if (!editor) return;
    const handler = (): void => {
      const md = (editor.storage as { markdown?: { getMarkdown?: () => string } }).markdown?.getMarkdown?.() ?? editor.getText();
      onBlurRef.current?.(md);
    };
    editor.on('blur', handler);
    return () => {
      editor.off('blur', handler);
    };
  }, [editor]);

  useEffect(() => {
    setCodeDraft(value);
  }, [value]);

  const toggleCodeView = () => {
    if (!editor) {
      setShowCode((v) => !v);
      return;
    }
    if (!showCode) {
      const md = (editor.storage as { markdown?: { getMarkdown?: () => string } }).markdown?.getMarkdown?.() ?? '';
      setCodeDraft(md);
      setShowCode(true);
    } else {
      editor.commands.setContent(codeDraft || '', { emitUpdate: false });
      echoNextRef.current = true;
      onChange(codeDraft);
      setShowCode(false);
    }
  };

  // Компактный тулбар форматирования (операции StarterKit). onMouseDown с
  // preventDefault обязателен: без него клик по кнопке уводит фокус из
  // ProseMirror → срабатывает editor.on('blur') → parent сохраняет и
  // размонтирует редактор ДО выполнения команды. С preventDefault фокус
  // остаётся в тексте, команда применяется к выделению.
  const tbBtn = (
    key: string,
    icon: JSX.Element,
    title: string,
    run: () => void,
    active?: boolean,
  ) => (
    <Tooltip title={title} key={key}>
      <Button
        type="text"
        size="small"
        icon={icon}
        onMouseDown={(e) => e.preventDefault()}
        onClick={run}
        // Компактная высота (26px вместо controlHeightSM=30) — тулбар ниже,
        // меньше «прыжок» блоков ниже при входе в правку markdown-поля.
        style={{
          height: 26,
          minWidth: 26,
          padding: '0 5px',
          ...(active ? { color: 'var(--color-primary)', background: 'var(--color-primary-bg)' } : null),
        }}
      />
    </Tooltip>
  );

  const tbSep = (key: string) => (
    <span key={key} style={{ width: 1, height: 16, background: 'var(--color-border)', margin: '0 3px', flex: 'none' }} />
  );

  return (
    <div className="lens-md-inline" style={{ borderRadius: 8, background: 'var(--color-bg-container)', width: '100%' }}>
      {editor && (
        <div
          className="lens-md-inline-toolbar"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            padding: '1px 6px',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          {!showCode && (
          <>
          {tbBtn('bold', <BoldOutlined />, 'Жирный (Ctrl+B)', () => editor.chain().focus().toggleBold().run(), editor.isActive('bold'))}
          {tbBtn('italic', <ItalicOutlined />, 'Курсив (Ctrl+I)', () => editor.chain().focus().toggleItalic().run(), editor.isActive('italic'))}
          {tbBtn('underline', <UnderlineOutlined />, 'Подчёркнутый (Ctrl+U)', () => editor.chain().focus().toggleUnderline().run(), editor.isActive('underline'))}
          {tbBtn('strike', <StrikethroughOutlined />, 'Зачёркнутый', () => editor.chain().focus().toggleStrike().run(), editor.isActive('strike'))}
          {tbBtn('code', <CodeOutlined />, 'Inline-код', () => editor.chain().focus().toggleCode().run(), editor.isActive('code'))}
          <ColorPickerButton editor={editor} kind="text" buttonStyle={{ height: 26, minWidth: 26, padding: '0 5px' }} />
          <ColorPickerButton editor={editor} kind="highlight" buttonStyle={{ height: 26, minWidth: 26, padding: '0 5px' }} />
          {tbSep('s1')}
          {tbBtn('h1', <span style={{ fontWeight: 600, fontSize: 12 }}>H1</span>, 'Заголовок 1', () => editor.chain().focus().toggleHeading({ level: 1 }).run(), editor.isActive('heading', { level: 1 }))}
          {tbBtn('h2', <span style={{ fontWeight: 600, fontSize: 12 }}>H2</span>, 'Заголовок 2', () => editor.chain().focus().toggleHeading({ level: 2 }).run(), editor.isActive('heading', { level: 2 }))}
          {tbBtn('h3', <span style={{ fontWeight: 600, fontSize: 12 }}>H3</span>, 'Заголовок 3', () => editor.chain().focus().toggleHeading({ level: 3 }).run(), editor.isActive('heading', { level: 3 }))}
          {tbSep('s2')}
          {tbBtn('ul', <UnorderedListOutlined />, 'Маркированный список', () => editor.chain().focus().toggleBulletList().run(), editor.isActive('bulletList'))}
          {tbBtn('ol', <OrderedListOutlined />, 'Нумерованный список', () => editor.chain().focus().toggleOrderedList().run(), editor.isActive('orderedList'))}
          {tbBtn('task', <CheckSquareOutlined />, 'Чек-лист', () => editor.chain().focus().toggleTaskList().run(), editor.isActive('taskList'))}
          {tbBtn('quote', <span style={{ fontWeight: 600 }}>“”</span>, 'Цитата', () => editor.chain().focus().toggleBlockquote().run(), editor.isActive('blockquote'))}
          {tbSep('s3')}
          {tbBtn('link', <LinkOutlined />, 'Ссылка', () => {
            const prev = (editor.getAttributes('link') as { href?: string }).href ?? '';
            const url = window.prompt('URL ссылки', prev);
            if (url === null) return;
            if (url === '') editor.chain().focus().extendMarkRange('link').unsetLink?.().run();
            else editor.chain().focus().extendMarkRange('link').setLink?.({ href: url }).run();
          }, editor.isActive('link'))}
          {tbBtn('hr', <MinusOutlined />, 'Горизонтальная линия', () => editor.chain().focus().setHorizontalRule().run())}
          {tbSep('s4')}
          {tbBtn('undo', <UndoOutlined />, 'Отменить (Ctrl+Z)', () => editor.chain().focus().undo().run())}
          {tbBtn('redo', <RedoOutlined />, 'Повторить (Ctrl+Shift+Z)', () => editor.chain().focus().redo().run())}
          </>
          )}
          {/* Переключатель «Код ↔ Визуальный» — в правом углу того же тулбара,
              чтобы у редактора была ОДНА полоса, а не ещё и нижняя (меньше
              «прыжок» блоков ниже при входе в правку). */}
          <span style={{ flex: 1 }} />
          {tbBtn(
            'codeview',
            showCode ? <EyeOutlined /> : <CodeOutlined />,
            showCode ? 'Визуальный редактор' : 'Показать markdown-код',
            toggleCodeView,
            showCode,
          )}
        </div>
      )}
      {showCode ? (
        <Input.TextArea
          value={codeDraft}
          onChange={(e) => {
            setCodeDraft(e.target.value);
            echoNextRef.current = true;
            onChange(e.target.value);
          }}
          onBlur={() => onBlurRef.current?.(codeDraft)}
          autoSize={{ minRows: 4, maxRows: 16 }}
          placeholder={placeholder ?? 'markdown…'}
          style={{ border: 'none', resize: 'none', fontFamily: 'monospace', fontSize: 13 }}
        />
      ) : (
        <div
          className="lens-tiptap-wrap markdown-field"
          style={{ minHeight: 40, padding: '4px 8px', border: 'none', borderRadius: 0 }}
          onMouseDown={(e) => {
            // Клик по padding'у обёртки или пустому месту ниже последнего параграфа
            // не доходит до ProseMirror и не ставит курсор. Если фокус ещё не в редакторе —
            // форсим focus в конец документа.
            if (!editor || editor.isFocused) return;
            const target = e.target as HTMLElement;
            if (target.classList.contains('lens-tiptap-wrap') || target.classList.contains('ProseMirror')) {
              e.preventDefault();
              editor.commands.focus('end');
            }
          }}
          onKeyDown={(e) => {
            // Escape выходит из редактора, сохраняя набранное: blur вызывает
            // onBlur → parent делает PATCH. stopPropagation обязателен — иначе
            // событие долетает до Modal карточки записи и закрывает её вместе
            // с несохранённым текстом.
            if (e.key !== 'Escape' || !editor) return;
            e.stopPropagation();
            editor.commands.blur();
          }}
        >
          <EditorContent editor={editor} />
        </div>
      )}
    </div>
  );
}
